import { useMemo } from 'react'
import { useAuthStore } from '@/store/auth.store'
import type { PermissionEntityType } from '@/api/model'

/**
 * Central client-side permission-enforcement primitive (Port of the old
 * `usePermissionChecker`). The backend is the real authority — this only
 * mirrors the logged-in user's own grants to hide/disable UI they can't use.
 *
 * A permission is the triple `(entityType, permissionName, targetEntityId|null)`:
 *  - `targetEntityId === null` → general/global grant (all entities of that type
 *    or a page-level capability).
 *  - `targetEntityId === <number>` → per-entity grant (just that one instance).
 */
export function usePermissions() {
  const permissions = useAuthStore((s) => s.currentUserPermissions)

  const lookup = useMemo(() => {
    // exact "entityType.permissionName[.targetEntityId]" → true
    const map = new Set<string>()
    // "entityType.permissionName" → list of granted targetEntityIds
    const perEntity = new Map<string, number[]>()

    for (const p of permissions) {
      if (!p.permissionName) continue
      const base = `${p.entityType}.${p.permissionName}`
      if (p.targetEntityId != null) {
        map.add(`${base}.${p.targetEntityId}`)
        const ids = perEntity.get(base) ?? []
        ids.push(p.targetEntityId)
        perEntity.set(base, ids)
      } else {
        map.add(base)
      }
    }

    return { map, perEntity }
  }, [permissions])

  /**
   * Exact match.
   * - Without `targetEntityId`: checks *only* the general grant (page-level /
   *   global capability). Does NOT consider per-entity grants.
   * - With `targetEntityId`: checks *only* that specific per-entity grant. Does
   *   NOT fall back to the general grant — use `userHasEntityPermission` for
   *   "general OR this id".
   */
  const userHasPermission = (
    entityType: PermissionEntityType,
    permissionName: string,
    targetEntityId?: number,
  ) => {
    const key =
      targetEntityId != null
        ? `${entityType}.${permissionName}.${targetEntityId}`
        : `${entityType}.${permissionName}`
    return lookup.map.has(key)
  }

  /**
   * True if the user has the general grant *or any* per-entity grant of this
   * kind. Used to decide whether to show a menu entry / list at all (there's
   * *something* to see).
   */
  const userHasAnyPermission = (
    entityType: PermissionEntityType,
    permissionName: string,
  ) => {
    const base = `${entityType}.${permissionName}`
    return lookup.map.has(base) || (lookup.perEntity.get(base)?.length ?? 0) > 0
  }

  /**
   * The common per-entity action idiom: the general grant OR the grant scoped
   * to this specific entity id. Used for row/entity action buttons.
   */
  const userHasEntityPermission = (
    entityType: PermissionEntityType,
    permissionName: string,
    targetEntityId: number,
  ) => {
    return (
      userHasPermission(entityType, permissionName) ||
      userHasPermission(entityType, permissionName, targetEntityId)
    )
  }

  return {
    userHasPermission,
    userHasAnyPermission,
    userHasEntityPermission,
  }
}
